import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Search, HardHat, Phone, Mail, DollarSign, Edit2, Trash2, X, Save, ChevronDown, ChevronUp, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatNzPhone, formatGstNumber } from '@/lib/formatters';
import ContractorUpliftFields from '@/components/ContractorUpliftFields';

const PHONE_KEYS = new Set(['contact_number']);
const GST_KEYS = new Set(['gst_number']);

// Fallback list — overwritten on first load by /api/dropdowns/contractor_specialties
const FALLBACK_SPECIALTIES = ["Concrete", "Longrun Iron", "Tin Tile", "Butynol", "Decramastic", "Roof Painter", "Roof Cleaner", "Roof Washing", "Cleaning", "Spouting"];

export default function ContractorsPage() {
  const { api } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [taskCatalog, setTaskCatalog] = useState([]);
  const [specialties, setSpecialties] = useState(FALLBACK_SPECIALTIES);
  const [newSpecialtyDraft, setNewSpecialtyDraft] = useState('');

  // Refetch specialties whenever the modal opens so freshly-added ones appear instantly
  useEffect(() => {
    if (!showForm) return;
    api.get('/dropdowns/contractor_specialties').then(r => {
      const opts = Array.isArray(r.data) ? r.data : (r.data?.options || []);
      if (opts.length) setSpecialties(opts);
    }).catch(() => {});
  }, [api, showForm]);

  const addSpecialty = async () => {
    const v = newSpecialtyDraft.trim();
    if (!v) return;
    if (!specialties.some(s => s.toLowerCase() === v.toLowerCase())) {
      try {
        const { data } = await api.post('/dropdowns/contractor_specialties/add', { value: v });
        const next = Array.isArray(data) ? data : specialties.concat(v);
        setSpecialties(next);
      } catch { setSpecialties(specialties.concat(v)); }
    }
    setForm(f => ({ ...f, specialties: [...new Set([...(f.specialties || []), v])] }));
    setNewSpecialtyDraft('');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: tc }] = await Promise.all([
        api.get(`/contractors?search=${search}`),
        api.get('/task-catalog?active=true'),
      ]);
      setContractors(data);
      setTaskCatalog(tc);
    } catch { /* ignore */ }
    setLoading(false);
  }, [api, search]);

  useEffect(() => { load(); }, [load]);

  const openForm = (c = null) => {
    if (c) {
      setForm({ ...c });
      setRates(c.rates || []);
      setEditId(c._id);
    } else {
      setForm({ contractor_name: '', company_name: '', contact_number: '', email: '', address: '', gst_number: '', gst_registered: false, specialties: [], default_hourly_rate: 0, bonus_rate: 0, default_m2_rate: 0, default_share: '', notes: '', active: true });
      setRates([]);
      setEditId(null);
    }
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = { ...form, rates, specialties: form.specialties || [], default_hourly_rate: parseFloat(form.default_hourly_rate) || 0, bonus_rate: parseFloat(form.bonus_rate) || 0, default_m2_rate: parseFloat(form.default_m2_rate) || 0, default_share: form.default_share || '', quality_uplift_percent: parseFloat(form.quality_uplift_percent) || 0, loyalty_uplift_percent: parseFloat(form.loyalty_uplift_percent) || 0, total_jobs_completed: parseInt(form.total_jobs_completed, 10) || 0, quality_tier: form.quality_tier || 'Bronze', start_date: form.start_date || '', loyalty_uplift_percent_override: !!form.loyalty_uplift_percent_override };
    if (editId) await api.put(`/contractors/${editId}`, payload);
    else await api.post('/contractors', payload);
    setShowForm(false); setEditId(null); setForm({}); setRates([]); load();
  };

  const addRate = () => setRates([...rates, { task_name: '', rate: 0, unit: 'hour', task_catalog_id: '' }]);
  const updateRate = (i, field, val) => { const r = [...rates]; r[i] = { ...r[i], [field]: field === 'rate' ? parseFloat(val) || 0 : val }; setRates(r); };
  const removeRate = (i) => setRates(rates.filter((_, idx) => idx !== i));

  const onRateTaskSelect = (i, taskId) => {
    const t = taskCatalog.find(x => x._id === taskId);
    if (t) {
      const r = [...rates];
      r[i] = { ...r[i], task_name: t.name, task_catalog_id: t._id, rate: r[i].rate || t.default_rate || 0, unit: t.default_unit || r[i].unit };
      setRates(r);
    }
  };

  const toggleSpecialty = (s) => {
    const current = form.specialties || [];
    if (current.includes(s)) setForm({ ...form, specialties: current.filter(x => x !== s) });
    else setForm({ ...form, specialties: [...current, s] });
  };

  return (
    <div data-testid="contractors-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Contractors</h1>
          <p className="text-sm text-slate-500 mt-1">{contractors.length} contractors with rate cards</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            data-testid="rate-matrix-link"
            to="/admin/component-labour-rates"
            className="flex items-center gap-2 px-3 py-2 border border-[#E2E2DF] hover:border-[#D35400] hover:text-[#D35400] text-sm rounded-lg text-slate-600 transition-colors"
          >
            <Database size={14} /> Rate matrix
          </Link>
          <button data-testid="add-contractor-button" onClick={() => openForm()} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-lg text-sm font-medium transition-colors">
            <Plus size={14} /> Add Contractor
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="contractor-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contractors..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#D35400]" />
      </div>

      {loading ? <div className="text-center py-12 text-slate-400 text-sm">Loading...</div> : (
        <div className="space-y-3">
          {contractors.map(c => (
            <div key={c._id} data-testid={`contractor-card-${c._id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-4 px-5 py-4 cursor-pointer" onClick={() => setExpandedId(expandedId === c._id ? null : c._id)}>
                <div className="w-10 h-10 rounded-lg bg-[#D35400]/10 flex items-center justify-center"><HardHat size={18} className="text-[#D35400]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#0F172A]">{c.contractor_name}</span>
                    {c.company_name && <span className="text-xs text-slate-500">({c.company_name})</span>}
                    {c.is_owner && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-bold">OWNER</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500">
                    {c.contact_number && <span className="flex items-center gap-1"><Phone size={10} />{c.contact_number}</span>}
                    {c.email && <span className="flex items-center gap-1"><Mail size={10} />{c.email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {(c.specialties || []).length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {(c.specialties || []).slice(0, 3).map(s => (
                        <span key={s} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full">{s}</span>
                      ))}
                    </div>
                  )}
                  {c.default_hourly_rate > 0 && <span className="text-xs font-mono text-slate-500">${c.default_hourly_rate}/hr{c.bonus_rate > 0 ? ` (+$${c.bonus_rate} bonus)` : ''}</span>}
                  <span className="text-xs text-slate-400">{(c.rates || []).length} rates</span>
                  <button onClick={(e) => { e.stopPropagation(); openForm(c); }} className="p-1.5 text-slate-400 hover:text-[#D35400] hover:bg-orange-50 rounded transition-colors"><Edit2 size={14} /></button>
                  {expandedId === c._id ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                </div>
              </div>

              {expandedId === c._id && (c.rates || []).length > 0 && (
                <div className="border-t border-slate-100 px-5 py-3">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1"><DollarSign size={12} /> Rate Card</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {(c.rates || []).map((r, i) => (
                      <div key={i} className="p-2 bg-slate-50 rounded-lg">
                        <div className="text-xs font-medium text-[#0F172A]">{r.task_name || 'General'}</div>
                        <div className="text-sm font-bold text-[#D35400] font-mono">${r.rate?.toFixed(2) || '0.00'}<span className="text-xs text-slate-400 font-normal">/{r.unit || 'hour'}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {contractors.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No contractors found</div>}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-bold text-[#0F172A]">{editId ? 'Edit' : 'New'} Contractor</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: 'contractor_name', l: 'Name *', r: true }, { k: 'company_name', l: 'Company' },
                  { k: 'contact_number', l: 'Phone' }, { k: 'email', l: 'Email' },
                  { k: 'address', l: 'Address' }, { k: 'gst_number', l: 'GST Number' },
                ].map(f => (
                  <div key={f.k}>
                    <label className="block text-xs font-medium text-slate-600 mb-1">{f.l}</label>
                    <input required={f.r} value={form[f.k] || ''} onChange={e => setForm({ ...form, [f.k]: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </div>
                ))}
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  <input
                    id="gst-registered-cb"
                    data-testid="contractor-gst-registered"
                    type="checkbox"
                    checked={!!form.gst_registered}
                    onChange={e => setForm({ ...form, gst_registered: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <label htmlFor="gst-registered-cb" className="text-xs font-medium text-slate-600">
                    GST registered <span className="text-slate-400">— when ticked, contractor sheets add 15% GST to payouts.</span>
                  </label>
                </div>
              </div>

              {/* Specialties */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Specialties <span className="text-slate-400 font-normal">(roofing trades)</span></label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {specialties.map(s => (
                    <button key={s} type="button" onClick={() => toggleSpecialty(s)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${(form.specialties || []).includes(s) ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>{s}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="contractor-new-specialty"
                    value={newSpecialtyDraft}
                    onChange={e => setNewSpecialtyDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSpecialty(); } }}
                    placeholder="Add a new speciality (e.g. Slate)…"
                    className="flex-1 px-3 py-1.5 border border-[#E2E2DF] rounded-md text-xs"
                  />
                  <button data-testid="add-specialty-btn" type="button" onClick={addSpecialty} disabled={!newSpecialtyDraft.trim()} className="px-3 py-1.5 bg-[#D35400] text-white text-xs font-semibold rounded-md hover:bg-[#A04000] disabled:opacity-40">+ Add</button>
                </div>
              </div>

              {/* Default Rates */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Hourly Rate ($)</label>
                  <input data-testid="contractor-hourly-rate" type="number" step="0.01" value={form.default_hourly_rate || ''} onChange={e => setForm({ ...form, default_hourly_rate: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-num" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Bonus Rate ($)</label>
                  <input data-testid="contractor-bonus-rate" type="number" step="0.01" value={form.bonus_rate || ''} onChange={e => setForm({ ...form, bonus_rate: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-num" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Premium-quality completion bonus per hour</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">m² Rate ($)</label>
                  <input data-testid="contractor-m2-rate" type="number" step="0.01" value={form.default_m2_rate || ''} onChange={e => setForm({ ...form, default_m2_rate: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono-num" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Default Share</label>
                  <input
                    data-testid="contractor-default-share"
                    value={form.default_share || ''}
                    onChange={e => setForm({ ...form, default_share: e.target.value })}
                    placeholder='e.g. "50%" or "Whole job"'
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">Pre-fills the Contractor Sheet PDF</p>
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_owner || false} onChange={e => setForm({ ...form, is_owner: e.target.checked })} className="w-4 h-4 accent-[#D35400] rounded" />
                    <span className="text-sm font-medium text-slate-700">Owner / Operator</span>
                  </label>
                </div>
              </div>
              {form.is_owner && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  Owner contractor costs are tracked for accurate job costing but classified as retained profit rather than a payable expense. No invoice is expected from this contractor.
                </div>
              )}

              {/* Rate Card */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-slate-600">Rate Card (per task)</label>
                  <button type="button" onClick={addRate} className="flex items-center gap-1 text-xs text-[#D35400] font-medium hover:text-[#B84500]"><Plus size={12} /> Add Rate</button>
                </div>
                {rates.length > 0 && (
                  <div className="space-y-2">
                    {rates.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <select value={r.task_catalog_id || ''} onChange={e => onRateTaskSelect(i, e.target.value)} className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm">
                          <option value="">{r.task_name || 'Select task...'}</option>
                          {taskCatalog.map(t => <option key={t._id} value={t._id}>{t.name} - {t.category}</option>)}
                        </select>
                        <input type="number" step="0.01" value={r.rate} onChange={e => updateRate(i, 'rate', e.target.value)} className="w-24 px-3 py-1.5 border border-slate-200 rounded-lg text-sm" placeholder="Rate" />
                        <select value={r.unit} onChange={e => updateRate(i, 'unit', e.target.value)} className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm">
                          {['hour', 'm2', 'lm', 'each', 'fixed'].map(u => <option key={u}>{u}</option>)}
                        </select>
                        <button type="button" onClick={() => removeRate(i)} className="p-1 text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                <input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>

              <ContractorUpliftFields
                api={api}
                contractorId={editId}
                form={form}
                onChange={partial => setForm(f => ({ ...f, ...partial }))}
              />

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
                <button data-testid="save-contractor-btn" type="submit" className="flex items-center gap-1.5 px-5 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-lg text-sm font-medium"><Save size={14} /> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
